import React from "react";
import PageLinks from "@/components/common/PageLinks";
import Preloader from "@/components/common/Preloader";
import FooterOne from "@/components/layout/footers/FooterOne";
import Header from "@/components/layout/headers/Header";
import MetaComponent from "@/components/common/MetaComponent";

const metadata = {
  title: "Privacy Policy || gvkEDU",
  description: "Privacy Policy for gvkEDU - Learn how we collect, use, and protect your personal information.",
};

export default function PrivacyPolicyPage() {
  const [links] = React.useState([
    {
      title: "Home",
      target: "/",
    },
    {
      title: "Privacy Policy",
      target: "/privacy-policy",
    },
  ]);

  return (
    <div className="main-content">
      <MetaComponent meta={metadata} />
      <Preloader />
      <Header />
      <div className="content-wrapper js-content-wrapper overflow-hidden">
        <PageLinks links={links} />
        
        <section className="layout-pt-lg layout-pb-lg bg-white">
          <div className="container">
            <div className="row justify-center">
              <div className="col-xl-10 col-lg-11">
                <div className="text-dark-1">
                  <h1 className="text-45 fw-700 mb-30">Privacy Policy</h1>
                  <p className="text-16 mb-30">
                    <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>

                  <div className="privacy-content">
                    <section className="mb-40">
                      <h2 className="text-30 fw-600 mb-20">1. Introduction</h2>
                      <p className="text-16 mb-15">
                        Welcome to gvkEDU (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and services.
                      </p>
                      <p className="text-16 mb-15">
                        By using our application, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
                      </p>
                    </section>

                    <section className="mb-40">
                      <h2 className="text-30 fw-600 mb-20">2. Information We Collect</h2>
                      
                      <h3 className="text-22 fw-600 mb-15 mt-30">2.1 Personal Information</h3>
                      <p className="text-16 mb-15">
                        We collect information that you provide directly to us, including:
                      </p>
                      <ul className="text-16 mb-15" style={{ paddingLeft: '30px' }}>
                        <li className="mb-10">Name (first name and last name)</li>
                        <li className="mb-10">Email address</li>
                        <li className="mb-10">Mobile/Phone number</li>
                        <li className="mb-10">Password (stored securely using encryption)</li>
                        <li className="mb-10">Address, State, and Country</li>
                        <li className="mb-10">Profile image (if uploaded)</li>
                        <li className="mb-10">Account preferences and settings</li>
                      </ul>

                      <h3 className="text-22 fw-600 mb-15 mt-30">2.2 Usage Information</h3>
                      <p className="text-16 mb-15">
                        We automatically collect certain information when you use our application:
                      </p>
                      <ul className="text-16 mb-15" style={{ paddingLeft: '30px' }}>
                        <li className="mb-10">Device information (device type, operating system, unique device identifiers)</li>
                        <li className="mb-10">Log data (IP address, access times, pages viewed)</li>
                        <li className="mb-10">Usage patterns (features used, time spent on the app)</li>
                        <li className="mb-10">Purchase history and transaction records</li>
                        <li className="mb-10">Last login timestamps</li>
                      </ul>

                      <h3 className="text-22 fw-600 mb-15 mt-30">2.3 Educational Content Interaction</h3>
                      <p className="text-16 mb-15">
                        We collect information related to your use of our educational content:
                      </p>
                      <ul className="text-16 mb-15" style={{ paddingLeft: '30px' }}>
                        <li className="mb-10">Exam and subject access history</li>
                        <li className="mb-10">Video viewing history and progress</li>
                        <li className="mb-10">Material downloads</li>
                        <li className="mb-10">Mock test attempts and results</li>
                        <li className="mb-10">Course progress and completion status</li>
                      </ul>
                    </section>

                    <section className="mb-40">
                      <h2 className="text-30 fw-600 mb-20">3. How We Use Your Information</h2>
                      <p className="text-16 mb-15">
                        We use the collected information for various purposes:
                      </p>
                      <ul className="text-16 mb-15" style={{ paddingLeft: '30px' }}>
                        <li className="mb-10">
                          <strong>Service Delivery:</strong> To provide, maintain, and improve our educational services, including exam preparation materials, videos, and mock tests.
                        </li>
                        <li className="mb-10">
                          <strong>Account Management:</strong> To create and manage your account, process registrations, and authenticate users.
                        </li>
                        <li className="mb-10">
                          <strong>Purchase Processing:</strong> To process payments, manage purchases, and provide access to purchased content.
                        </li>
                        <li className="mb-10">
                          <strong>Communication:</strong> To send you important updates, notifications, login OTP emails, and respond to your inquiries.
                        </li>
                        <li className="mb-10">
                          <strong>Personalization:</strong> To personalize your learning experience and recommend relevant content.
                        </li>
                        <li className="mb-10">
                          <strong>Analytics:</strong> To analyze usage patterns, improve our services, and develop new features.
                        </li>
                        <li className="mb-10">
                          <strong>Security:</strong> To detect, prevent, and address technical issues, fraud, and security threats.
                        </li>
                        <li className="mb-10">
                          <strong>Legal Compliance:</strong> To comply with legal obligations and enforce our terms of service.
                        </li>
                      </ul>
                    </section>

                    <section className="mb-40">
                      <h2 className="text-30 fw-600 mb-20">4. Information Sharing and Disclosure</h2>
                      <p className="text-16 mb-15">
                        We do not sell your personal information. We may share your information only in the following circumstances:
                      </p>
                      
                      <h3 className="text-22 fw-600 mb-15 mt-30">4.1 Service Providers</h3>
                      <p className="text-16 mb-15">
                        We may share information with third-party service providers who perform services on our behalf, such as:
                      </p>
                      <ul className="text-16 mb-15" style={{ paddingLeft: '30px' }}>
                        <li className="mb-10">Cloud storage providers (AWS S3) for video and content hosting</li>
                        <li className="mb-10">Email service providers for sending notifications</li>
                        <li className="mb-10">Payment processors for handling transactions</li>
                        <li className="mb-10">Analytics providers to understand app usage</li>
                      </ul>

                      <h3 className="text-22 fw-600 mb-15 mt-30">4.2 Legal Requirements</h3>
                      <p className="text-16 mb-15">
                        We may disclose your information if required by law or in response to valid requests by public authorities.
                      </p>

                      <h3 className="text-22 fw-600 mb-15 mt-30">4.3 Business Transfers</h3>
                      <p className="text-16 mb-15">
                        In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
                      </p>

                      <h3 className="text-22 fw-600 mb-15 mt-30">4.4 With Your Consent</h3>
                      <p className="text-16 mb-15">
                        We may share your information with your explicit consent or at your direction.
                      </p>
                    </section>

                    <section className="mb-40">
                      <h2 className="text-30 fw-600 mb-20">5. Data Security</h2>
                      <p className="text-16 mb-15">
                        We implement appropriate technical and organizational security measures to protect your personal information:
                      </p>
                      <ul className="text-16 mb-15" style={{ paddingLeft: '30px' }}>
                        <li className="mb-10">Passwords are encrypted using bcrypt hashing algorithms</li>
                        <li className="mb-10">Data transmission is secured using HTTPS/TLS encryption</li>
                        <li className="mb-10">Access controls and authentication mechanisms are in place</li>
                        <li className="mb-10">Regular security assessments and updates</li>
                        <li className="mb-10">Secure cloud storage with AWS S3</li>
                      </ul>
                      <p className="text-16 mb-15">
                        However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security.
                      </p>
                    </section>

                    <section className="mb-40">
                      <h2 className="text-30 fw-600 mb-20">6. Data Retention</h2>
                      <p className="text-16 mb-15">
                        We retain your personal information for as long as necessary to:
                      </p>
                      <ul className="text-16 mb-15" style={{ paddingLeft: '30px' }}>
                        <li className="mb-10">Provide our services to you</li>
                        <li className="mb-10">Comply with legal obligations</li>
                        <li className="mb-10">Resolve disputes and enforce our agreements</li>
                        <li className="mb-10">Maintain accurate records of purchases and transactions</li>
                      </ul>
                      <p className="text-16 mb-15">
                        When you delete your account, we will delete or anonymize your personal information, except where we are required to retain it for legal or legitimate business purposes.
                      </p>
                    </section>

                    <section className="mb-40">
                      <h2 className="text-30 fw-600 mb-20">7. Your Rights and Choices</h2>
                      <p className="text-16 mb-15">
                        Depending on your location, you may have certain rights regarding your personal information:
                      </p>
                      <ul className="text-16 mb-15" style={{ paddingLeft: '30px' }}>
                        <li className="mb-10">
                          <strong>Access:</strong> Request access to your personal information
                        </li>
                        <li className="mb-10">
                          <strong>Correction:</strong> Request correction of inaccurate or incomplete information
                        </li>
                        <li className="mb-10">
                          <strong>Deletion:</strong> Request deletion of your personal information
                        </li>
                        <li className="mb-10">
                          <strong>Portability:</strong> Request a copy of your data in a portable format
                        </li>
                        <li className="mb-10">
                          <strong>Opt-out:</strong> Opt-out of certain data collection or processing activities
                        </li>
                        <li className="mb-10">
                          <strong>Account Settings:</strong> Update your account information and preferences through the app settings
                        </li>
                      </ul>
                      <p className="text-16 mb-15">
                        To exercise these rights, please contact us at <a href="mailto:info@gvkedu.com" className="text-purple-1">info@gvkedu.com</a>.
                      </p>
                    </section>

                    <section className="mb-40">
                      <h2 className="text-30 fw-600 mb-20">8. Children&apos;s Privacy</h2>
                      <p className="text-16 mb-15">
                        Our services are not intended for children under the age of 13 (or the applicable age of consent in your jurisdiction). We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
                      </p>
                    </section>

                    <section className="mb-40">
                      <h2 className="text-30 fw-600 mb-20">9. Third-Party Links and Services</h2>
                      <p className="text-16 mb-15">
                        Our application may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to read their privacy policies before providing any information.
                      </p>
                    </section>

                    <section className="mb-40">
                      <h2 className="text-30 fw-600 mb-20">10. International Data Transfers</h2>
                      <p className="text-16 mb-15">
                        Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. By using our services, you consent to the transfer of your information to these countries.
                      </p>
                    </section>

                    <section className="mb-40">
                      <h2 className="text-30 fw-600 mb-20">11. Changes to This Privacy Policy</h2>
                      <p className="text-16 mb-15">
                        We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the &quot;Last Updated&quot; date. You are advised to review this Privacy Policy periodically for any changes.
                      </p>
                      <p className="text-16 mb-15">
                        Material changes will be notified through the application or via email to the address associated with your account.
                      </p>
                    </section>

                    <section className="mb-40">
                      <h2 className="text-30 fw-600 mb-20">12. Contact Us</h2>
                      <p className="text-16 mb-15">
                        If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                      </p>
                      <div className="text-16 mb-15" style={{ paddingLeft: '20px' }}>
                        <p className="mb-10">
                          <strong>Email:</strong> <a href="mailto:info@gvkedu.com" className="text-purple-1">info@gvkedu.com</a>
                        </p>
                        <p className="mb-10">
                          <strong>Organization:</strong> gvkEDU
                        </p>
                      </div>
                    </section>

                    <section className="mb-40">
                      <h2 className="text-30 fw-600 mb-20">13. Consent</h2>
                      <p className="text-16 mb-15">
                        By using our application, you consent to our Privacy Policy and agree to its terms. If you do not agree to this policy, please do not use our services.
                      </p>
                    </section>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <FooterOne />
      </div>
    </div>
  );
}

